import { Button } from '@base-ui/react/button';
import { faFileLines, faSliders } from '@fortawesome/free-solid-svg-icons';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { TranslationLinksPanel } from '@/admin/content/TranslationLinksPanel';
import { apiErrorDetail } from '@/admin/errorDetail';
import { LANGUAGE_LABELS } from '@/admin/languages';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { SeriesHeader } from '@/admin/ui/ContentNav';
import { DeleteContent } from '@/admin/ui/DeleteContent';
import {
  DetailPage,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelGroup,
  PanelLabel,
  SaveGroup,
} from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import {
  CheckboxLabel,
  ErrorText,
  FieldHint,
  FormLabel,
  FormRow,
  InfoNotice,
  Select,
  TextInput,
  WarningNotice,
} from '@/admin/ui/Form';
import { RichContent } from '@/admin/ui/RichContent';
import { RichTextEditor } from '@/admin/ui/RichTextEditor';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import { useUnsavedChanges } from '@/admin/ui/useUnsavedChanges';
import { EditConflictNotice, editConflict } from '@/admin/ui/Versions';
import { CertificateTemplateControl } from '@/admin/widgets/CertificateTemplateControl';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';

type SeriesAdmin = components['schemas']['SeriesAdmin'];

function invalidateSeries(seriesId: number) {
  queryClient.invalidateQueries({
    queryKey: api.adminGetSeries.getQueryKey({ path: { series_id: seriesId } }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminListSeries.getQueryKey({}),
  });
}

// "POSTAL_STUDENT" -> "postal student"; the server sends the enum, not a label.
function profileTypeLabel(type: string): string {
  return type.toLowerCase().replaceAll('_', ' ');
}

export const Route = createFileRoute('/admin/series/$seriesId')({
  staticData: { minTier: 'ADMIN' },
  component: SeriesDetail,
  validateSearch: validateEditSearch,
});

function SeriesDetail() {
  const { t } = useTranslation();
  const { seriesId } = Route.useParams();
  const seriesIdNum = Number(seriesId);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  const admin = api.adminGetSeries.useQuery({
    path: { series_id: seriesIdNum },
  });

  if (admin.isLoading) return <Message>{t('admin.series.loading')}</Message>;
  if (admin.error || !admin.data)
    return <Message $error>{t('admin.series.errorLoading')}</Message>;

  const series = admin.data;
  const certificateTitle = series.certificate_template_title;
  const forbidden = series.online_forbidden_profile_types;

  return (
    <DetailPage>
      <SeriesHeader
        series={series}
        nav={!editing}
        action={
          !editing && (
            <Button className={buttonStyles.Button} onClick={startEditing}>
              {t('admin.common.edit')}
            </Button>
          )
        }
      />

      {editing ? (
        <SeriesEditor key={series.id} series={series} onDone={stopEditing} />
      ) : (
        <>
          <Section>
            <SectionTitle>{t('admin.common.overview')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{series.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.language')}</FieldLabel>
                <FieldValue>{t(LANGUAGE_LABELS[series.language])}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.slug')}</FieldLabel>
                <FieldValue>{series.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.series.position')}</FieldLabel>
                <FieldValue>{series.position}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.status')}</FieldLabel>
                <FieldValue>{series.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.series.courseCount')}</FieldLabel>
                <FieldValue>{series.course_count}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.series.selfPaced')}</FieldLabel>
                <FieldValue>
                  {series.is_self_paced
                    ? t('admin.common.yes')
                    : t('admin.common.no')}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.series.starter')}</FieldLabel>
                <FieldValue>
                  {series.is_starter
                    ? t('admin.common.yes')
                    : t('admin.common.no')}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.series.inSequence')}</FieldLabel>
                <FieldValue>
                  {series.course_progression_in_sequence
                    ? t('admin.common.yes')
                    : t('admin.common.no')}
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.series.onlineStudy')}</FieldLabel>
                <FieldValue>
                  {forbidden.length === 0
                    ? t('admin.series.onlineStudyAll')
                    : t('admin.series.onlineStudyForbidden', {
                        types: forbidden.map(profileTypeLabel).join(', '),
                        interpolation: { escapeValue: false },
                      })}
                </FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>{t('admin.series.description')}</SectionTitle>
            {series.content ? (
              <Description
                // biome-ignore lint/security/noDangerouslySetInnerHtml: admin-authored series HTML
                dangerouslySetInnerHTML={{ __html: series.content }}
              />
            ) : (
              <Empty>{t('admin.series.noDescription')}</Empty>
            )}
          </Section>

          <Section>
            <SectionTitle>{t('admin.series.certificate')}</SectionTitle>
            <CertificateRow>
              <div>
                <FieldValue>
                  {certificateTitle !== null ? (
                    <>
                      {t('admin.series.certificateEarn')}{' '}
                      <CertificateName>{certificateTitle}</CertificateName>.
                    </>
                  ) : (
                    t('admin.series.noCertificate')
                  )}
                </FieldValue>
              </div>
              <CertificateTemplateControl
                owner={{ type: 'series', id: series.id }}
                hasTemplate={certificateTitle !== null}
                backfillable={series.certificate_backfillable}
                defaultTitle={series.title}
                onChanged={() => invalidateSeries(series.id)}
              />
            </CertificateRow>
          </Section>

          <Section>
            <SectionTitle>
              {t('admin.content.translations.heading')}
            </SectionTitle>
            <TranslationLinksPanel
              kind="series"
              id={series.id}
              language={series.language}
              translations={series.translations}
              editable
              onChanged={() => invalidateSeries(series.id)}
            />
          </Section>
        </>
      )}
    </DetailPage>
  );
}

// Only one series per language is the Master Series; turning it on elsewhere
// clears it here. Names the holder so the editor sees what they are replacing.
function useCurrentMasterSeries(series: SeriesAdmin, enabled: boolean) {
  const [holder, setHolder] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const list = await api.adminListSeries.fetchQuery({
        parameters: {
          query: { language: series.language, limit: 50, offset: 0 },
        },
      });
      for (const row of list.data) {
        if (row.id === series.id) continue;
        const detail = await api.adminGetSeries.fetchQuery({
          parameters: { path: { series_id: row.id } },
        });
        if (detail.is_starter) {
          if (!cancelled) setHolder(detail.title);
          return;
        }
      }
      if (!cancelled) setHolder(null);
    })().catch(() => {
      if (!cancelled) setHolder(null);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, series.id, series.language]);
  return holder;
}

function SeriesEditor({
  series,
  onDone,
}: {
  series: SeriesAdmin;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(series.title);
  const [slug, setSlug] = useState(series.slug);
  const [status, setStatus] = useState(series.status);
  const [content, setContent] = useState(series.content);
  const [selfPaced, setSelfPaced] = useState(series.is_self_paced);
  const [starter, setStarter] = useState(series.is_starter);
  const [inSequence, setInSequence] = useState(
    series.course_progression_in_sequence,
  );
  const [confirming, setConfirming] = useState(false);
  const [confirmingStarter, setConfirmingStarter] = useState(false);

  const formValues = JSON.stringify([
    title,
    slug,
    status,
    content,
    selfPaced,
    starter,
    inSequence,
  ]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const {
    allowLeave,
    confirmDiscard,
    dialog: unsavedDialog,
  } = useUnsavedChanges(dirty);

  // Pinned when editing starts, so a background refetch cannot advance it past
  // someone else's save.
  const [baseRevision] = useState(series.revision);

  const updateMutation = api.adminUpdateSeries.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      invalidateSeries(series.id);
      onDone();
    },
  });

  const navigate = useNavigate();
  const deleteMutation = api.adminDeleteSeries.useMutation(undefined, {
    meta: { toast: t('admin.common.deleted') },
    onSuccess: () => {
      allowLeave();
      queryClient.removeQueries({
        queryKey: api.adminGetSeries.getQueryKey({
          path: { series_id: series.id },
        }),
      });
      queryClient.invalidateQueries({
        queryKey: api.adminListSeries.getQueryKey({}),
      });
      navigate({ to: '/admin/series/' });
    },
  });

  const trimmedTitle = title.trim();
  // Publishing is one-way (the server rejects published -> draft), so an
  // already-published series shows no status control at all.
  const isPublished = series.status === 'published';
  const willPublish = !isPublished && status === 'published';
  const becomesStarter = starter && !series.is_starter;
  const holder = useCurrentMasterSeries(series, becomesStarter);

  // Each confirmation re-enters with what the editor has already agreed to, so a
  // series that is both publishing and becoming the Master Series asks twice, once.
  const save = (publishOk: boolean, starterOk: boolean) => {
    if (!trimmedTitle || updateMutation.isPending) return;
    if (willPublish && !publishOk) {
      setConfirming(true);
      return;
    }
    if (becomesStarter && !starterOk) {
      setConfirmingStarter(true);
      return;
    }
    // Only what changed: the server reacts to each toggle (Master Series clears
    // another row; in-sequence rewrites prerequisites).
    updateMutation.mutate({
      path: { series_id: series.id },
      body: {
        title: trimmedTitle,
        slug,
        status,
        ...(content !== series.content ? { content } : {}),
        ...(selfPaced !== series.is_self_paced
          ? { is_self_paced: selfPaced }
          : {}),
        ...(starter !== series.is_starter ? { is_starter: starter } : {}),
        ...(inSequence !== series.course_progression_in_sequence
          ? { course_progression_in_sequence: inSequence }
          : {}),
        expected_revision: baseRevision,
      },
    });
  };
  const handleSave = () => save(false, false);

  const reload = () => {
    allowLeave();
    invalidateSeries(series.id);
    onDone();
  };

  return (
    <EditorLayout>
      <EditorMain>
        <FormSection
          tone="content"
          icon={faFileLines}
          title={t('admin.common.details')}
          description={t('admin.series.detailsDescription')}
        >
          <FormRow>
            <FormLabel htmlFor="series-title">
              {t('admin.common.title')}
            </FormLabel>
            <TextInput
              id="series-title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="series-slug">
              {t('admin.common.slug')}
            </FormLabel>
            <TextInput
              id="series-slug"
              value={slug}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSlug(e.target.value)
              }
            />
          </FormRow>
        </FormSection>

        <FormSection
          tone="feedback"
          icon={faFileLines}
          title={t('admin.series.description')}
          description={t('admin.series.descriptionHint')}
        >
          <RichTextEditor
            value={series.content}
            onChange={setContent}
            ariaLabel={t('admin.series.descriptionAria')}
          />
        </FormSection>

        <FormSection
          tone="settings"
          icon={faSliders}
          title={t('admin.series.settings')}
          description={t('admin.series.settingsHint')}
        >
          <FormRow>
            <CheckboxLabel htmlFor="series-self-paced">
              <input
                id="series-self-paced"
                type="checkbox"
                checked={selfPaced}
                onChange={(e) => setSelfPaced(e.target.checked)}
              />
              {t('admin.series.selfPaced')}
            </CheckboxLabel>
            <FieldHint>{t('admin.series.selfPacedHint')}</FieldHint>
          </FormRow>
          <FormRow>
            <CheckboxLabel htmlFor="series-starter">
              <input
                id="series-starter"
                type="checkbox"
                checked={starter}
                onChange={(e) => setStarter(e.target.checked)}
              />
              {t('admin.series.starter')}
            </CheckboxLabel>
            <FieldHint>{t('admin.series.starterHint')}</FieldHint>
          </FormRow>
          <FormRow>
            <CheckboxLabel htmlFor="series-in-sequence">
              <input
                id="series-in-sequence"
                type="checkbox"
                checked={inSequence}
                onChange={(e) => setInSequence(e.target.checked)}
              />
              {t('admin.series.inSequence')}
            </CheckboxLabel>
            <FieldHint>{t('admin.series.inSequenceHint')}</FieldHint>
          </FormRow>
          <FormRow>
            <FormLabel as="div">{t('admin.series.onlineStudy')}</FormLabel>
            <InfoNotice>
              {series.online_forbidden_profile_types.length === 0
                ? t('admin.series.onlineStudyAll')
                : t('admin.series.onlineStudyForbidden', {
                    types: series.online_forbidden_profile_types
                      .map(profileTypeLabel)
                      .join(', '),
                    interpolation: { escapeValue: false },
                  })}
            </InfoNotice>
          </FormRow>
        </FormSection>
      </EditorMain>

      <EditorPanel aria-label={t('admin.series.saveAndSettings')}>
        <SaveGroup
          heading={<PanelLabel>{t('admin.nav.series')}</PanelLabel>}
          dirty={dirty}
          saving={updateMutation.isPending}
          saveDisabled={!trimmedTitle}
          onSave={handleSave}
          onCancel={() => confirmDiscard(onDone)}
        >
          {!trimmedTitle && (
            <FieldHint>{t('admin.series.giveTitle')}</FieldHint>
          )}
          {updateMutation.isError &&
            (editConflict(updateMutation.error) ? (
              <EditConflictNotice
                error={updateMutation.error}
                onReload={reload}
              />
            ) : (
              <ErrorText>
                {apiErrorDetail(updateMutation.error) ??
                  t('admin.common.couldNotSave')}
              </ErrorText>
            ))}
        </SaveGroup>

        <PanelGroup>
          <FormRow>
            <FormLabel htmlFor="series-status">
              {t('admin.common.status')}
            </FormLabel>
            {isPublished ? (
              <InfoNotice>{t('admin.series.publishedInfo')}</InfoNotice>
            ) : (
              <>
                <Select
                  id="series-status"
                  value={status}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setStatus(e.target.value as SeriesAdmin['status'])
                  }
                >
                  <option value="draft">draft</option>
                  <option value="published">published</option>
                </Select>
                {willPublish && (
                  <WarningNotice>
                    {t('admin.series.publishWarning')}
                  </WarningNotice>
                )}
              </>
            )}
          </FormRow>
        </PanelGroup>

        <DeleteContent
          noun="series"
          blocker={series.delete_blocker}
          pending={deleteMutation.isPending}
          error={deleteMutation.error}
          onDelete={() =>
            deleteMutation.mutate({ path: { series_id: series.id } })
          }
        />
      </EditorPanel>

      <ConfirmDialog
        open={confirming}
        title={t('admin.series.confirmTitle')}
        confirmLabel={t('admin.common.publish')}
        onConfirm={() => {
          setConfirming(false);
          save(true, false);
        }}
        onCancel={() => setConfirming(false)}
      >
        <p>{t('admin.series.confirmBody1')}</p>
        <p>{t('admin.series.confirmBody2')}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmingStarter}
        title={t('admin.series.starterConfirmTitle')}
        confirmLabel={t('admin.series.starterConfirm')}
        onConfirm={() => {
          setConfirmingStarter(false);
          save(true, true);
        }}
        onCancel={() => setConfirmingStarter(false)}
      >
        <p>
          {holder
            ? t('admin.series.starterConfirmReplaces', {
                title: series.title,
                holder,
                interpolation: { escapeValue: false },
              })
            : t('admin.series.starterConfirmNone', {
                title: series.title,
                interpolation: { escapeValue: false },
              })}
        </p>
        <p>{t('admin.series.starterConfirmEffect')}</p>
      </ConfirmDialog>
      {unsavedDialog}
    </EditorLayout>
  );
}

const Description = styled(RichContent)`
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const Empty = styled.p`
  color: var(--grey-600);
  margin: 0;
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const CertificateName = styled.strong`
  color: var(--grey-900);
  font-weight: 600;
`;

const CertificateRow = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
  padding: var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
